// AI_CONTEXT: {
//   "docs": [
//     "epics/202610211834_GBG_group-member-background/tickets/GBG-002-resolve-member-background-on-load"
//   ],
//   "purpose": "Group page: loads group, events and membership catalog, renders layouts, and starts the hero blank for signed-in visitors to membership groups until member art resolves",
//   "version": "1.1.0",
//   "priority": "core",
//   "stability": "stable"
// }
define("Pages/Group", [`Page`, `module`], function (Super, Self) {
	const SCROLLING_LAYOUTS = [`FLYER`, `GRID`, `LIST`];
	const DEFAULT_THEME = `DARK`;
	const EVENTS_PER_PAGE = 10;
	const INTERSECTION_CONTAINER_ID = `page-header-intersection-id`;

	return Super.inherit({
		namespace: Self.id,
		requires: [`Groups`, `MembershipsApi`, `System`, `XHR`],
		constructor(setup) {
			const { App, Config, Groups, MembershipsApi, container, data } = this;
			const { PRIMARY_DOMAIN } = Config;
			const { trackingOptions } = data;
			const expanding = true; // whether new events are currently being fetched
			const { groupHasMemberships } = setup;
			let { groupId } = setup;
			let promise;
			const membershipsPromise = MembershipsApi.getMembershipsByGroupId({ groupId }).catch(() => []);
			const hasMembershipsPromise = this.isBoolean(groupHasMemberships)
				? Promise.resolve(groupHasMemberships)
				: membershipsPromise.then((memberships) => this.isArray(memberships, 1));

			promise = this.when({
				promise: Groups.get({ id: groupId }),
				then: (group) => {
					const { id, layout, subdomain } = group;
					const theme = this.isString(group.theme, 1) ? group.theme : DEFAULT_THEME;
					const url = `${PRIMARY_DOMAIN}/groups/${subdomain}`;
					const title = `${group.name} Tickets & Events | Tixr`;
					// CALENDAR is paginated too: it fetches no events up front, but the deferred
					// fetch on the first List toggle must use the paginated endpoint like LIST does
					const paginatedLayouts = [`CALENDAR`, `GRID`, `LIST`];
					const promises = [];
					let moreEventsAvailable;
					let currentPage;
					// set only by the CALENDAR/LIST branch, whose mode depends on two independent
					// responses; every other layout resolves its mode inside its own handler
					let resolveMode;

					trackingOptions.pageType = `Group`;
					trackingOptions.groupId = group.id;
					data.uid = `g.${group.id}`;

					if (this.includes(paginatedLayouts, group.layout)) {
						moreEventsAvailable = true;
						currentPage = 1;
					} else {
						moreEventsAvailable = false;
						currentPage = null;
					}

					container.setAttribute(`theme`, theme);
					data.title = title;

					this.assign(data, {
						EVENTS_PER_PAGE,
						PRIMARY_DOMAIN,
						currentPage,
						dateSelection: null,
						displayCalendarListMenu: layout === `CALENDAR` || layout === `LIST`,
						expanding,
						group,
						// CALENDAR defers its event list to the first List toggle; every other
						// layout has its events by the time the page renders
						listEventsLoaded: layout !== `CALENDAR`,
						moreEventsAvailable,
						// whether onScroll is currently subscribed; App.onScroll does not de-dupe
						scrollHandlerAttached: false,
						navLinks: [],
						toggleOptionSelected: layout.toLowerCase(),
						theme,
						url,
					});

					if (layout === `CALENDAR` || layout === `LIST`) {
						// NOTE: the calendar and events responses resolve independently, so neither
						// handler decides `mode` on its own - whichever landed last used to win,
						// which made the empty state depend on response timing. Each records only
						// what it found; resolveMode() derives the mode once both are in.
						let hasCalendarPages = false;
						let hasEvents = false;

						// async handler
						const then = (pages) => {
							hasCalendarPages = this.isArray(pages, 1);

							if (hasCalendarPages) {
								data.pages = pages;
							}
						};
						// fetch the group's calendar
						const calendarPromise = Groups.getCalendarPages({ id, then });
						promises.push(calendarPromise);

						// NOTE: CALENDAR renders entirely from the calendar endpoints above and never
						// reads data.events, so it fetches none here - the List pane's paginated
						// fetch is deferred to the first toggle (see loadListEvents)
						if (layout === `LIST`) {
							// fetch the first batch of events
							const eventsPromise = this.getMoreEvents({
								then: (events) => {
									// NOTE: min length 1 - an empty array is still an array, and a
									// LIST group with no events belongs in the empty state rather
									// than behind a toggle and month bar over an empty pane
									hasEvents = this.isArray(events, 1);
									data.events = events;
								},
							});
							promises.push(eventsPromise);
						}

						resolveMode = () => {
							// `calendar` and `list` both render GroupCalendar from data.pages[0],
							// so neither is reachable without pages; LIST additionally needs
							// something to list
							const hasContent = hasCalendarPages && (layout === `CALENDAR` || hasEvents);

							data.mode = hasContent ? layout.toLowerCase() : `empty`;
						};
					} else {
						const layoutMode = layout === `CAROUSEL` ? `carousel` : `default`;

						// async handler
						const then = (events) => {
							data.mode = this.isArray(events) ? layoutMode : `empty`;
							data.events = events;
						};
						// fetch the first batch of events
						const eventsPromise = this.getMoreEvents({ then });
						promises.push(eventsPromise);
					}

					// `when` ignores a non-function `then`, so the other layouts pass through untouched
					return this.when({ promises, then: resolveMode });
				},
			});

			return this.when({
				promise,
				then: () => {
					const { displayCalendarListMenu, events, group, mode } = data;
					const { backgroundUrl, backgroundUrlv2, hiddenDetails, mobileBackgroundUrl, mobileBackgroundUrlv2 } = group;
					const { isNativeApp } = this.System.get();
					const showPrices = group.priceKey === `SHOW`;
					const promises = [];
					const backupBackgroundUrl = `${Config.STATIC_IMG_PATH}/group-bg.jpg`;
					const showSiteLinks = !this.includes(hiddenDetails, `FOOTER`);
					let promise;
					let clientSetBackgroundUrl = backgroundUrlv2;
					let clientSetMobileBackgroundUrl = mobileBackgroundUrlv2;

					if (this.isObject(group.carouselConfiguration) && this.isArray(group.carouselConfiguration.carousels, 1)) {
						group.carouselConfiguration.carousels = this.map(
							group.carouselConfiguration.carousels,
							({ items, ...rest }) => {
								return {
									...rest,
									items: this.map(items, (item) => {
										const { flyerUrl: portraitImageUrl, mobileImageUrl: imageUrlv2 } =
											this.find(events, { id: item.id }) || {};
										return { ...item, portraitImageUrl, imageUrlv2 };
									}),
								};
							}
						);
					}

					if (!this.isString(backgroundUrlv2, 1) && this.isString(backgroundUrl, 1)) {
						clientSetBackgroundUrl = backgroundUrl;
						this.addClass(`legacy-bg-desktop`);
					}

					if (!this.isString(mobileBackgroundUrlv2, 1) && this.isString(mobileBackgroundUrl, 1)) {
						clientSetMobileBackgroundUrl = mobileBackgroundUrl;
						this.addClass(`legacy-bg-mobile`);
					}

					this.assign(data, {
						backgroundUrl: clientSetBackgroundUrl || backupBackgroundUrl,
						mobileBackgroundUrl: clientSetMobileBackgroundUrl || backupBackgroundUrl,
					});

					this.render();

					// NOTE: displayCalendarListMenu is layout-derived, so it stays true for a
					// CALENDAR/LIST group that turned out to have nothing to show - but that page
					// renders neither the nav nor .content.calendar/.content.events, and
					// updateLayoutMode would hand DOM.show/DOM.hide null targets, which they reject
					if (displayCalendarListMenu && mode !== `empty`) {
						this.renderCalendarListMonths();
						this.updateLayoutMode(group.layout.toLowerCase(), false);
					}

					promises.push(
						this.create({
							namespace: `Modules/PageHeader`,
							setup: {
								name: `page-header`,
								desktopBackgroundWhenScrolled: ![`list`, `calendar`, `carousel`].includes(mode),
								hideOnMobileScroll: true,
								showTixrLogo: false,
								showUserLinks: false,
								// the page header is hidden in the native app, so its observer can't
								// measure itself — the page runs its own observer instead
								intersectionContainerId: isNativeApp ? null : INTERSECTION_CONTAINER_ID,
								leftLinks: [
									{
										type: `icon`,
										customClass: `circle-background`,
										mobile: true,
										desktop: true,
										app: false,
										href: `/`,
										icon: `arrow-left`,
									},
								],
								callbacks: {
									onScrollStateChange: ({ scrolled }) => {
										if (scrolled) {
											this.addClass(`scrolled`, container);
										} else {
											this.removeClass(`scrolled`, container);
										}
									},
									onHideStateChange: ({ hidden }) => {
										if (hidden) {
											this.addClass(`header-hidden`, container);
										} else {
											this.removeClass(`header-hidden`, container);
										}
									},
								},
							},
						}),
						this.create({
							namespace: `Modules/PageFooter`,
							setup: {
								name: `page-footer`,
								showSiteLinks,
							},
						}),
						hasMembershipsPromise.then((hasMemberships) => {
							const startBlank = hasMemberships && this.isString(App.getCookie(`ULIN`));

							return this.when({
								promises: [
									this.create({
										namespace: `Modules/ParallaxBlur`,
										setup: {
											name: `parallax-blur-desktop`,
											backgroundImageUrl: startBlank ? null : data.backgroundUrl,
											scrollContainer: this.container,
										},
									}),
									this.create({
										namespace: `Modules/ParallaxBlur`,
										setup: {
											name: `parallax-blur-mobile`,
											backgroundImageUrl: startBlank ? null : data.mobileBackgroundUrl,
											scrollContainer: this.container,
										},
									}),
								],
							});
						}),

						this.create({
							namespace: `Modules/ReadMore`,
							setup: {
								name: `description`,
								// Pages/Group/main.hbs already paints ReadMore's markup into this mount, so that
								// the description lands in the same frame as the rest of render() instead of
								// blinking out until this create() resolves. Adopt that markup rather than
								// re-rendering over it.
								prerendered: true,
								maxLinesMobile: 3,
								maxLinesDesktop: 3,
								content: group.description,
								heading: group.heroText || group.name,
								textSizeMobile: `medium`,
								textSizeDesktop: `medium`,
							},
						}),
						this.create({
							namespace: `Modules/MembershipCta`,
							setup: {
								name: `membership-cta`,
								groupId: group.id,
								groupName: group.name,
								groupLogoUrl: group.logoThumbnailUrl || group.logoUrl,
								callbacks: {
									onMembershipChange: (subscription) => this.applyMemberBackgrounds(subscription),
									onCtaVisibilityChange: (isVisible) => {
										// reserve bottom clearance so the fixed CTA doesn't cover page content
										if (isVisible) {
											this.addClass(`has-membership-cta`);
										} else {
											this.removeClass(`has-membership-cta`);
										}
									},
								},
							},
						})
					);

					data.expanding = false;

					switch (mode) {
						case `calendar`: // CALENDAR layout
						case `list`: // LIST layout
							promises.push(
								this.create({
									namespace: `Modules/GroupCalendar`,
									setup: {
										name: `group-calendar`,
										groupId: group.id,
										showPrices,
									},
									then: (calendar) => {
										calendar.renderPage({
											month: data.pages[0].month,
											year: data.pages[0].year,
										});
									},
								}),

								// FLYER, GRID, LIST layouts
								this.create({
									namespace: `Modules/EventList`,
									setup: {
										name: `event-list`,
										layout: `LIST`,
										showPrices: true, // not yet supported for non-CALENDAR layouts
										// `true` renders the empty list shell that addEvents() fills -
										// a CALENDAR page has no events yet, and an undefined/empty
										// value would render the module's empty message instead
										events: events || true,
									},
								})
							);
							break;

						case `carousel`: {
							// CAROUSEL layout
							const { carouselConfiguration } = group;
							const { carousels } = carouselConfiguration;
							promise = this.create({
								namespace: `Modules/CarouselList`,
								setup: {
									carousels,
									desktopMergeIntoHeader: true,
									mode: `ux3`,
									name: `carousel-list`,
									showAnchorLinks: true,
								},
							});

							promises.push(promise);
							break;
						}

						case `empty`:
							// no further setup required
							break;

						default:
							// FLYER, GRID, LIST layouts
							promise = this.create({
								namespace: `Modules/EventList`,
								setup: {
									name: `event-list`,
									layout: group.layout,
									showPrices: true, // not yet supported for non-CALENDAR layouts
									events,
								},
							});
							promises.push(promise);
					}

					return this.when({
						promises,
						then: () => {
							if (data.memberBackgrounds) {
								this.showMemberBackgrounds(data.memberBackgrounds);
							}
						},
					});
				},
			});
		},
		ready(setup) {
			const { App, DOM, data } = this;
			const { displayCalendarListMenu, group } = data;
			const { layout } = group;
			const isScrollingMode = this.includes(SCROLLING_LAYOUTS, layout);
			const urlParams = App.getQueryParams();

			const navListWrapper = this.get(`.month-list`);
			const listContentWrapper = this.get(`.event-list`);
			const navDimensions = DOM.getDimensions(listContentWrapper);

			this.mapNavLinks();
			this.setScrollStateObserver();

			if (displayCalendarListMenu) {
				App.onScroll(this.updateMonthBarStuckState);

				this.defer({
					then: this.updateMonthBarStuckState,
					duration: 100,
				});

				App.onScroll((evt) => {
					const isMobile = App.isMobileViewportSize();
					if (data.scrollingToAnchor || data.toggleOptionSelected !== `list`) {
						return;
					}

					const { viewportTop: navViewportTop } = navDimensions;
					const BUFFER = isMobile ? 75 : 40;
					let found = false;

					this.each(data.navLinks, ({ dimensions, navLink }, index) => {
						if (found) {
							// top visible carousel already found
							this.removeClass(`selected`, navLink);
						} else {
							// still looking for top visible carousel
							const { height: wrapperHeight, viewportTop: wrapperViewportTop } = dimensions;
							const viewportPosition = wrapperViewportTop + wrapperHeight - BUFFER;

							if (viewportPosition < BUFFER) {
								if (navViewportTop === 0 && index === 0) {
									this.addClass(`selected`, navLink);
									found = true;
								} else {
									this.removeClass(`selected`, navLink);
								}
							} else {
								if (!this.hasClass(`selected`, navLink)) {
									this.addClass(`selected`, navLink);

									const { offsetLeft: linkLeft } = navLink;
									const behavior = `smooth`;
									let left = linkLeft;

									if (isMobile) {
										const computedStyles = window.getComputedStyle(navListWrapper);
										const paddingLeftStyle = computedStyles[`padding-left`];
										const paddingLeftString = paddingLeftStyle.replace(/[^0-9.]/, ``);
										const paddingLeft = parseFloat(paddingLeftString);
										left = linkLeft - paddingLeft;
									}

									navListWrapper.scrollTo({ behavior, left });
								}
								found = true;
							}
						}
					});
				});
			}

			// NOTE: a CALENDAR page is not a scrolling mode at ready() time - it has fetched no
			// events yet. Its subscription is made by loadListEvents once the fan toggles to List.
			if (isScrollingMode && data.mode !== `empty`) {
				this.enableInfiniteScroll();
			}

			if (urlParams?.modal === `membership-checkout`) {
				App.setQueryParam(`modal`, null, true);
				this.instances[`membership-cta`]?.loadMembershipCheckoutOverlay();
			}
		},
		api: {
			applyMemberBackgrounds: {
				async value(subscription) {
					const { MembershipsApi, data } = this;

					data.memberBackgrounds = await MembershipsApi.resolveBackgroundImages({
						groupId: data.group.id,
						subscription,
						desktop: data.backgroundUrl,
						mobile: data.mobileBackgroundUrl,
					});
					this.showMemberBackgrounds(data.memberBackgrounds);
				},
			},

			showMemberBackgrounds: {
				value({ desktop, mobile }) {
					const { instances } = this;

					if (this.isObject(instances[`parallax-blur-desktop`])) {
						instances[`parallax-blur-desktop`].updateBackgroundImageUrl(desktop);
					}

					if (this.isObject(instances[`parallax-blur-mobile`])) {
						instances[`parallax-blur-mobile`].updateBackgroundImageUrl(mobile);
					}
				},
			},

			updateMonthBarStuckState: {
				value() {
					const { container } = this;
					const monthListBar = this.get(`.month-list-bar`);
					const stuckClass = `month-bar-stuck`;

					if (!this.isElement(monthListBar)) {
						return;
					}

					// The bar is stuck once its viewport top has reached its own sticky offset.
					// The page header's IntersectionObserver (which drives `scrolled`) resolves
					// later than that, so the bar's background is keyed off this instead - the
					// Calendar/List toggle then scrolls behind an opaque bar rather than through
					// a transparent one. Reading the used `top` covers both the resting offset
					// and the smaller one the `scrolled` state animates to.
					const stickyTop = parseFloat(window.getComputedStyle(monthListBar).top) || 0;
					const { top } = monthListBar.getBoundingClientRect();

					if (top <= stickyTop + 1) {
						this.addClass(stuckClass, container);
					} else {
						this.removeClass(stuckClass, container);
					}
				},
			},

			mapNavLinks: {
				value() {
					const { data, DOM } = this;
					const eventListContentWrapper = this.get(`.event-list`);
					const navListWrapper = this.get(`.month-list`);
					const navLinks = this.select(`[action="select-month"]`, navListWrapper);
					const navLinkWrappers = this.map(navLinks, (wrapper) => {
						const month = this.parseInt(wrapper.getAttribute(`data-month`));
						const year = this.parseInt(wrapper.getAttribute(`data-year`));
						const navLink = this.get(`[action="select-month"][data-month="${month}"][data-year="${year}"]`);
						const monthSection = this.get(`[data-month="${month}"][data-year="${year}"]`, eventListContentWrapper);
						const dimensions = DOM.getDimensions(monthSection);

						return { dimensions, monthSection, navLink };
					});

					data.navLinks = navLinkWrappers;
				},
			},

			/**
			 * Returns the height of the trigger band at the top of the viewport used by
			 * setScrollStateObserver. There is no page header in the native app, so the band is the
			 * observed element's own sticky parking offset rather than the page header height.
			 * NOTE: the `scrolled` class re-parks the element at `top: 0`, so its computed `top` is
			 * only meaningful while un-scrolled — measure then, otherwise reuse the cached value.
			 * @method getScrollStateBand
			 * @param {Element} target - the observed element
			 * @returns {Number} the band height, in pixels
			 */
			getScrollStateBand: {
				value(target) {
					const { container, data } = this;

					if (!this.isFinite(data.scrollStateBand) || !this.hasClass(`scrolled`, container)) {
						data.scrollStateBand = parseFloat(window.getComputedStyle(target).top) || 0;
					}

					return data.scrollStateBand;
				},
			},

			/**
			 * Toggles the `scrolled` class on the page as the intersection container reaches the top
			 * of the viewport. Only used in the native app: the page header is hidden there
			 * (`hide-app`), so its own observer measures a zero-height element and never fires,
			 * leaving its onScrollStateChange callback dead. On web that callback still drives this.
			 * @method setScrollStateObserver
			 */
			setScrollStateObserver: {
				value() {
					const { System, container, elements, instances } = this;
					const { isNativeApp } = System.get();

					this.destroyScrollStateObserver();

					if (!isNativeApp) {
						return;
					}

					const target = document.getElementById(INTERSECTION_CONTAINER_ID);

					if (!this.isElement(target)) {
						return;
					}

					// generation counter pattern to prevent stale observer callbacks after a reset
					const generation = (instances.scrollStateGeneration || 0) + 1;
					instances.scrollStateGeneration = generation;

					const band = this.getScrollStateBand(target);
					const { IntersectionObserver } = window;
					const handler = ([entry]) => {
						if (instances.scrollStateGeneration !== generation) {
							return;
						}

						if (entry.isIntersecting) {
							this.addClass(`scrolled`, container);
						} else {
							this.removeClass(`scrolled`, container);
						}
					};
					// shrink the root to a band at the top of the viewport. The extra pixel keeps a
					// band of 0 (desktop) from collapsing into a root that can never intersect
					const bottomMargin = Math.max(window.innerHeight - band - 1, 0);
					const options = {
						threshold: [0, 0.25, 0.5, 0.75, 1],
						rootMargin: `0px 0px -${bottomMargin}px 0px`,
					};
					const observer = new IntersectionObserver(handler, options);

					observer.observe(target);

					elements.scrollStateTarget = target;
					instances.scrollStateObserver = observer;
				},
			},

			destroyScrollStateObserver: {
				value() {
					const { instances } = this;
					const { scrollStateObserver } = instances;

					if (this.isObject(scrollStateObserver)) {
						scrollStateObserver.disconnect();
						instances.scrollStateObserver = null;
					}
				},
			},

			getMoreEvents: {
				async: true,
				defaults: {
					// scroll-driven pagination must never block the UI; the deferred List load
					// opts in, since it is an explicit fan action with nothing to show until it lands
					block: false,
				},
				value(options) {
					const { Groups, data } = this;
					const { EVENTS_PER_PAGE, currentPage, group } = data;
					const [id, page] = [group.id, currentPage];
					const requestOptions = {
						id,
						block: options.block,
						then: (events) => {
							if (events.length < EVENTS_PER_PAGE) {
								data.moreEventsAvailable = false;
							}

							return events;
						},
					};

					if (this.isInt(currentPage)) {
						requestOptions.page = page;
						data.currentPage = page + 1;
					}

					return Groups.getEventsById(requestOptions);
				},
			},

			/**
			 * Fetch and render the List pane's first page of events, once, for a CALENDAR-layout
			 * group that deferred it. A no-op for every other layout and on every toggle after
			 * the first, so the fan's previously loaded pages survive switching back and forth.
			 */
			loadListEvents: {
				async: true,
				value() {
					const { data, instances } = this;

					if (data.listEventsLoaded) {
						return;
					}

					// latch before awaiting so a double-click cannot start a second fetch.
					// NOTE: not reset on failure - getMoreEvents has already advanced currentPage
					// by then, so a retry would request page 2 and silently skip page 1. A failed
					// deferred load leaves an empty list until the page is reloaded, which is the
					// same outcome a LIST-layout group already has when its page-1 fetch fails.
					data.listEventsLoaded = true;

					return this.getMoreEvents({
						block: true, // blocking spinner for the duration of the deferred fetch
						then: (events) => {
							const eventList = instances[`event-list`];

							data.events = events;

							// the group has calendar months but nothing to list - drop the
							// calendar/list UI for the page-level empty state
							if (!this.isArray(events, 1)) {
								this.showEmptyState();
								return;
							}

							// absent when the group had no calendar months and the page rendered
							// the empty state instead of the calendar/list container
							if (this.isObject(eventList)) {
								eventList.addEvents({ events, transition: `none` });

								// the month anchors are only measurable once their sections exist, so
								// every append has to remap - until now each anchor's monthSection lookup
								// found nothing and it carried no usable dimensions
								this.mapNavLinks();

								// safe to subscribe now: getMoreEvents has already set
								// moreEventsAvailable from this page's size
								this.enableInfiniteScroll();
							}
						},
					});
				},
			},

			renderCalendarListMonths: {
				value() {
					const { data } = this;
					const { pages } = data;

					this.each(pages, (page, index) => {
						const { events, month, year } = page;
						const monthList = this.get(`.month-list`);
						const monthItem = document.createElement(`a`);
						const eventsElement = document.createElement(`div`);

						monthItem.setAttribute(`action`, `select-month`);
						monthItem.setAttribute(`data-month`, month);
						monthItem.setAttribute(`data-year`, year);
						monthItem.setAttribute(`href`, `#`);

						this.addClass(`month-item`, monthItem);
						this.addClass(`event-quantity`, eventsElement);

						if (index === 0) {
							this.addClass(`selected`, monthItem);
							data.dateSelection = { month, year };
						}

						const date = new Date(year, month - 1, 1);
						const monthName = new Intl.DateTimeFormat(`en-US`, { month: `short` }).format(date);
						monthItem.textContent = monthName + ` ` + year;
						eventsElement.textContent = events;
						monthItem.appendChild(eventsElement);
						monthList.appendChild(monthItem);
					});

					this.updateScrollArrows();
				},
			},

			/**
			 * Replace the calendar/list UI with the page-level empty state, in place. Used when
			 * a CALENDAR group's deferred List load comes back with no events - the calendar
			 * itself has months to show, but the group has nothing on sale, so the page says so.
			 *
			 * Deliberately a targeted DOM swap rather than a re-render: Module#render replaces
			 * container.innerHTML wholesale, which would destroy the mount points of the
			 * page-header, page-footer, parallax-blur and description modules and leave those
			 * instances bound to detached elements.
			 */
			showEmptyState: {
				value() {
					const { DOM, data, instances } = this;
					const pageHeader = instances[`page-header`];
					const toggleNav = this.get(`.group-calendar-list-toggle-wrapper`);
					const listCalendarContainer = this.get(`.list-calendar-container`);

					// already empty - either swapped once already, or the page rendered empty at
					// construction because the group had no calendar months at all
					if (data.mode === `empty` || !this.isElement(listCalendarContainer)) {
						return;
					}

					data.mode = `empty`;

					// the empty state carries id="page-header-intersection-id" itself, since the
					// nav that normally owns it is removed just below
					const markup = this.render({
						name: `noEvents`,
						target: null, // return the markup, don't write it anywhere yet
					});

					listCalendarContainer.insertAdjacentHTML(`beforebegin`, markup);
					DOM.remove(listCalendarContainer);

					if (this.isElement(toggleNav)) {
						DOM.remove(toggleNav);
					}

					// the month anchors pointed into the list that just went away
					data.navLinks = [];

					// PageHeader resolves its observer target by id at call time, so it has to be
					// rebuilt now that a different element carries the id - the existing observer
					// is still bound to the node we just detached and would never fire again
					if (this.isObject(pageHeader)) {
						pageHeader.setIntersectionObserver();
					}
				},
			},

			/**
			 * Subscribe onScroll for infinite pagination, once. Called from ready() for the
			 * layouts that have their events at first paint, and from loadListEvents for a
			 * CALENDAR group that only acquires a List pane when the fan toggles to it.
			 */
			enableInfiniteScroll: {
				value() {
					const { App, data } = this;

					// Base#onScroll pushes onto the subscriber list without de-duping, so a
					// second registration would double every page request from here on
					if (data.scrollHandlerAttached || !data.moreEventsAvailable) {
						return;
					}

					data.scrollHandlerAttached = true;
					App.onScroll(this.onScroll);

					this.defer({
						then: this.onScroll,
						duration: 100,
					});
				},
			},

			onScroll: {
				value(evt) {
					const { App, DOM, data, instances } = this;
					const { displayCalendarListMenu, expanding, moreEventsAvailable, toggleOptionSelected } = data;

					if (!moreEventsAvailable) {
						data.scrollHandlerAttached = false;
						App.offScroll(this.onScroll);
					}

					if (!moreEventsAvailable || expanding) {
						return;
					}

					// On the shared Calendar/List page the list pane can be hidden while the
					// scroll trigger - a sibling of the toggled container - stays in viewport.
					// Paginating then would fetch events the calendar never shows. Bail without
					// unsubscribing: the fan may toggle back to List and still need pagination.
					// NOTE: gated on displayCalendarListMenu because toggleOptionSelected is
					// seeded from the configured layout, so it reads `grid`/`flyer` on the
					// scrolling layouts that have no toggle at all.
					if (displayCalendarListMenu && toggleOptionSelected !== `list`) {
						return;
					}

					const cushion = 50;
					const target = this.get(`.scroll-trigger`);
					const inViewport = DOM.inViewport({ cushion, target });

					if (inViewport) {
						data.expanding = true;

						return this.getMoreEvents({
							then: (events) => {
								const eventList = instances[`event-list`];
								eventList.addEvents({ events });

								// the month anchors are only measurable once their sections exist, so every
								// append has to remap - a month first seen on page 2+ would otherwise keep the
								// dimensions it was measured with while its section was still missing
								this.mapNavLinks();

								return this.defer({
									duration: 100,
									then: () => (data.expanding = false),
								});
							},
						});
					}
				},
			},

			updateMonthSelected: {
				value() {
					const { data, instances } = this;
					const { dateSelection } = data;
					const { month, year } = dateSelection;
					const calendar = instances[`group-calendar`];

					if (this.isObject(calendar)) {
						calendar.renderPage({ month, year });
					}
				},
			},

			updateScrollArrows: {
				debounce: 200,
				value(options = { skipCallbacks: false }) {
					const { data } = this;
					const { pages } = data;

					if (this.isArray(pages, 1)) {
						const { DOM, callbacks } = this;
						// const { items, list } = elements;
						const list = this.get(`.month-list`);
						const listItems = this.select(`.month-item`, list);
						const leftArrowElement = this.get(`[action="scroll-left"]`);
						const rightArrowElement = this.get(`[action="scroll-right"]`);
						const arrowContainer = this.get(`.arrow-container`);
						const disabledClass = `disabled`;
						const firstElement = listItems[0];
						const lastElement = listItems[listItems.length - 1];
						const epsilon = 1;
						let shouldShowLeftArrow = false;
						let showRightArrow = false;

						DOM.deferUICalculations(list, () => {
							const listDimensions = DOM.getDimensions(list);

							shouldShowLeftArrow =
								listDimensions.left + listDimensions.paddingLeft - DOM.getDimensions(firstElement).left > epsilon;

							if (
								DOM.getDimensions(lastElement).right - (listDimensions.right - listDimensions.paddingRight) >
								epsilon
							) {
								showRightArrow = true;
							}

							if (shouldShowLeftArrow || showRightArrow) {
								this.show({ target: leftArrowElement });
								this.show({ target: rightArrowElement });
								this.show({ target: arrowContainer });
							} else {
								this.hide({ target: leftArrowElement });
								this.hide({ target: rightArrowElement });
								this.hide({ target: arrowContainer });
							}

							if (shouldShowLeftArrow) {
								this.removeClass(disabledClass, leftArrowElement);
							} else {
								this.addClass(disabledClass, leftArrowElement);
							}

							if (showRightArrow) {
								this.removeClass(disabledClass, rightArrowElement);
							} else {
								this.addClass(disabledClass, rightArrowElement);
							}

							let closestElementIndex = 0;
							let closestElementDistance = Infinity;
							// find centered most element index
							this.each(pages, (item, index) => {
								const itemDimensions = DOM.getDimensions(item);
								const itemCenter = itemDimensions.left + itemDimensions.width / 2;

								// get device center x
								const deviceCenter = window.innerWidth / 2;
								const distance = Math.abs(itemCenter - deviceCenter);

								if (distance < closestElementDistance) {
									closestElementIndex = index;
									closestElementDistance = distance;
								}
							});

							if (!options.skipCallbacks && this.isFunction(callbacks.onIndexChange)) {
								callbacks.onIndexChange({
									index: closestElementIndex,
									displayLeftTrigger: shouldShowLeftArrow,
									displayRightTrigger: showRightArrow,
								});
							}
						});
					}
				},
			},

			updateLayoutMode: {
				async value(layoutMode, scrollToContent = true) {
					const { App, DOM, data } = this;
					const calendarToggleButton = this.get(`[action="show-calendar"]`);
					const listToggleButton = this.get(`[action="show-list"]`);
					const calendarElement = this.get(`.content.calendar`);
					const listElement = this.get(`.content.events`);

					data.toggleOptionSelected = layoutMode;

					switch (layoutMode) {
						case `calendar`: {
							// using list dimensions since calendar is "0" when hidden
							const listDimensions = DOM.getDimensions(listElement);
							// mobile sticky stack is the page header + the month bar only - the
							// Calendar/List toggle scrolls away rather than pinning with it
							const buffer = App.isMobileViewportSize() ? 105 : 60;
							const offset = listDimensions.top - buffer;
							this.addClass(`active`, calendarToggleButton);
							this.removeClass(`active`, listToggleButton);
							this.show({ target: calendarElement });
							this.hide({ target: listElement });

							if (scrollToContent) {
								App.scrollTo({ top: offset, behavior: `smooth` });
							}
							break;
						}
						case `list`:
							this.addClass(`active`, listToggleButton);
							this.removeClass(`active`, calendarToggleButton);
							this.hide({ target: calendarElement });
							// must land before selectMonth below, which scrolls to a month wrapper
							// that does not exist in the DOM until the events have rendered
							await this.loadListEvents();

							// that load may have swapped the page to the empty state, taking the
							// month bar and the list pane with it - there is nothing left to
							// reveal, and selectMonth would be handed a null month item
							if (data.mode === `empty`) {
								break;
							}

							await this.show({ target: listElement });
							this.selectMonth(this.get(`.month-item.selected`), scrollToContent);
							break;
					}
				},
			},

			selectMonth: {
				async value(target, scrollToMonth = true) {
					const { data, App, DOM } = this;
					const { toggleOptionSelected } = data;
					const month = this.parseInt(target.getAttribute(`data-month`));
					const year = this.parseInt(target.getAttribute(`data-year`));
					const monthList = this.get(`.month-list`);
					const previoslySelected = this.get(`.month-item.selected`, this.container);

					this.removeClass(`selected`, previoslySelected);
					this.addClass(`selected`, target);

					data.dateSelection = { month, year };
					this.updateMonthSelected();

					// scroll the selected month into view
					const { left: itemLeft } = target.getBoundingClientRect();
					const { left: containerLeft } = monthList.getBoundingClientRect();
					const scrollAmount = itemLeft - containerLeft - 32;
					monthList.scrollBy({ left: scrollAmount, behavior: `smooth` });

					if (toggleOptionSelected === `list`) {
						// scroll list to first event of the month
						const eventList = this.get(`.event-list`);
						const monthString = month.toString();
						const yearString = year.toString();
						const firstEventOfMonth = this.get(`[data-month="${monthString}"][data-year="${yearString}"]`, eventList);
						let scrollElement = firstEventOfMonth;

						if (!this.isElement(firstEventOfMonth)) {
							const scrollTrigger = this.get(`.scroll-trigger`);

							if (!this.isElement(scrollTrigger)) {
								return;
							}

							scrollElement = scrollTrigger;
						}

						// mobile sticky stack is the page header + the month bar only - the
						// Calendar/List toggle scrolls away rather than pinning with it
						const offset = App.isMobileViewportSize() ? 125 : 90;
						const elementDimensions = DOM.getDimensions(scrollElement);
						const top = elementDimensions.top - offset;

						data.scrollingToAnchor = true;

						if (scrollToMonth) {
							await App.scrollTo({
								top: top,
								behavior: `smooth`,
							});
						}

						data.scrollingToAnchor = false;
					}
				},
			},
		},

		destructor(options) {
			this.destroyScrollStateObserver();
		},

		listeners: [
			{
				event: `resize`,
				target: window,
				matchContainer: false,
				debounce: 200,
				handler() {
					// the band and the viewport height both feed the observer's rootMargin
					this.setScrollStateObserver();
				},
			},
			{
				event: `click`,
				filter: `[action="select-month"]`,
				handler(evt) {
					const target = evt.currentTarget;
					this.selectMonth(target);
				},
			},
			{
				event: `click`,
				filter: `[action="scroll-right"]`,
				handler(evt) {
					// find the next month not fully visible
					const monthList = this.get(`.month-list`);
					const monthItems = this.select(`.month-item`, monthList);
					const { right: containerRight } = monthList.getBoundingClientRect();
					let targetItem;

					this.each(monthItems, (item) => {
						const { right: itemRight } = item.getBoundingClientRect();

						if (itemRight > containerRight && !this.isDefined(targetItem)) {
							targetItem = item;
						}
					});

					if (this.isDefined(targetItem)) {
						const { left: itemLeft } = targetItem.getBoundingClientRect();
						const { left: containerLeft } = monthList.getBoundingClientRect();
						const scrollAmount = itemLeft - containerLeft - 32;
						monthList.scrollBy({ left: scrollAmount, behavior: `smooth` });
					}
				},
			},
			{
				event: `click`,
				filter: `[action="scroll-left"]`,
				handler(evt) {
					const monthList = this.get(`.month-list`);
					const monthItems = this.select(`.month-item`, monthList);
					const { left: containerLeft } = monthList.getBoundingClientRect();
					let targetItem;

					this.each(monthItems, (item) => {
						const { left: itemLeft } = item.getBoundingClientRect();
						if (itemLeft < containerLeft) {
							targetItem = item;
						}
					});

					if (this.isDefined(targetItem)) {
						const { right: itemRight } = targetItem.getBoundingClientRect();
						const { right: containerRight } = monthList.getBoundingClientRect();
						const scrollAmount = itemRight - containerRight + 32;
						monthList.scrollBy({ left: scrollAmount, behavior: `smooth` });
					}
				},
			},
			{
				event: `scroll`,
				target: `.month-list`,
				handler() {
					this.updateScrollArrows();
				},
			},
			{
				event: `click`,
				filter: `[action="show-calendar"]`,
				handler(evt) {
					this.updateLayoutMode(`calendar`);
				},
			},
			{
				event: `click`,
				filter: `[action="show-list"]`,
				handler(evt) {
					this.updateLayoutMode(`list`);
				},
			},
		],
	});
});

define("Pages/Group.templates",["handlebars"], function(Handlebars) {

this["Templates"] = this["Templates"] || {};

Handlebars.registerPartial("GroupEmptyState", Handlebars.template({"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    return " <div id=\"page-header-intersection-id\" class=\"content alert\"> <h2 class=\"transition-content\">No upcoming shows</h2> <h3 class=\"transition-content\">Watch this page for future events!</h3> </div> ";
},"useData":true}));

this["Templates"]["Pages/Group/events"] = Handlebars.template({"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    return "<div class=\"content\"> </div>";
},"useData":true});

this["Templates"]["Pages/Group/main"] = Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " <div class=\"logo transition-content\"><img src=\""
    + alias2(alias1(((stack1 = (depth0 != null ? lookupProperty(depth0,"group") : depth0)) != null ? lookupProperty(stack1,"heroUrl") : stack1), depth0))
    + "\" alt=\""
    + alias2(alias1(((stack1 = (depth0 != null ? lookupProperty(depth0,"group") : depth0)) != null ? lookupProperty(stack1,"name") : stack1), depth0))
    + "\"/></div> ";
},"3":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " <div class=\"hero-text transition-content\">"
    + container.escapeExpression(container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"group") : depth0)) != null ? lookupProperty(stack1,"heroText") : stack1), depth0))
    + "</div> ";
},"5":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " "
    + ((stack1 = lookupProperty(helpers,"compare").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"mode") : depth0),"empty","!=",{"name":"compare","hash":{},"fn":container.program(6, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":2884},"end":{"line":1,"column":3789}}})) != null ? stack1 : "")
    + " ";
},"6":function(container,depth0,helpers,partials,data) {
    return " <nav class=\"group-calendar-list-toggle-wrapper\" aria-label=\"Calendar navigation\">  <div id=\"page-header-intersection-id\" class=\"month-list-bar\"> <div class=\"group-calendar-list-toggle-background\"></div> <div class=\"month-list\"></div> <div class=\"arrow-container hide-mobile\"> <a href=\"#\" action=\"scroll-left\"><span class=\"icon icon-arrow-left\" aria-hidden=\"true\"></span></a> <a href=\"#\" action=\"scroll-right\"><span class=\"icon icon-arrow-right\" aria-hidden=\"true\"></span></a> </div> </div> <div class=\"calendar-list-toggle\"> <a href=\"#\" class=\"toggle-button active\" action=\"show-calendar\">Calendar</a> <a href=\"#\" class=\"toggle-button\" action=\"show-list\">List</a> </div> </nav> ";
},"8":function(container,depth0,helpers,partials,data) {
    return " <div class=\"list-calendar-container\"> <div class=\"content calendar transition-content\"> <div name=\"group-calendar\"></div> </div> <div class=\"content events LIST transition-content\"> <div name=\"event-list\"></div> </div> </div> ";
},"10":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"compare").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"mode") : depth0),"list",{"name":"compare","hash":{},"fn":container.program(8, data, 0),"inverse":container.program(11, data, 0),"data":data,"loc":{"start":{"line":1,"column":4053},"end":{"line":1,"column":4772}}})) != null ? stack1 : "");
},"11":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"compare").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"mode") : depth0),"carousel",{"name":"compare","hash":{},"fn":container.program(12, data, 0),"inverse":container.program(14, data, 0),"data":data,"loc":{"start":{"line":1,"column":4308},"end":{"line":1,"column":4772}}})) != null ? stack1 : "");
},"12":function(container,depth0,helpers,partials,data) {
    return " <div id=\"page-header-intersection-id\" class=\"content carousels transition-content\"> <div name=\"carousel-list\"></div> </div> ";
},"14":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"compare").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"mode") : depth0),"empty",{"name":"compare","hash":{},"fn":container.program(15, data, 0),"inverse":container.program(17, data, 0),"data":data,"loc":{"start":{"line":1,"column":4465},"end":{"line":1,"column":4772}}})) != null ? stack1 : "");
},"15":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "  "
    + ((stack1 = container.invokePartial(lookupProperty(partials,"GroupEmptyState"),depth0,{"name":"GroupEmptyState","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + " ";
},"17":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "  <div id=\"page-header-intersection-id\" class=\"content events "
    + container.escapeExpression(container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"group") : depth0)) != null ? lookupProperty(stack1,"layout") : stack1), depth0))
    + " transition-content\"> <div name=\"event-list\"></div> </div> ";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.lambda, alias2=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " <header class=\"head-fixed\"> <div name=\"page-header\" class=\"hide-app\"></div> </header> <div name=\"parallax-blur-desktop\" class=\"hide-mobile\"></div> <div name=\"parallax-blur-mobile\" class=\"hide-desktop\"></div> <div name=\"background-gradient\"> <div class=\"color-gradient\"></div> </div> <main class=\"body\"> <div class=\"head-fixed\">  <h1 class=\"visually-hidden\">Tixr - "
    + container.escapeExpression(alias1(((stack1 = (depth0 != null ? lookupProperty(depth0,"group") : depth0)) != null ? lookupProperty(stack1,"name") : stack1), depth0))
    + "</h1> </div> <div class=\"hero-wrapper\"> <div class=\"content compact hero\"> "
    + ((stack1 = lookupProperty(helpers,"if").call(alias2,((stack1 = (depth0 != null ? lookupProperty(depth0,"group") : depth0)) != null ? lookupProperty(stack1,"heroUrl") : stack1),{"name":"if","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":514},"end":{"line":1,"column":638}}})) != null ? stack1 : "")
    + " "
    + ((stack1 = lookupProperty(helpers,"if").call(alias2,((stack1 = (depth0 != null ? lookupProperty(depth0,"group") : depth0)) != null ? lookupProperty(stack1,"heroText") : stack1),{"name":"if","hash":{},"fn":container.program(3, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":639},"end":{"line":1,"column":736}}})) != null ? stack1 : "")
    + "  <div name=\"description\" class=\"transition-content Module ReadMore text-size-mobile-medium text-size-desktop-medium\"> <div class=\"text-content\" style=\"-webkit-line-clamp: 3; -webkit-box-orient: vertical; display: -webkit-box; overflow: hidden; text-overflow: ellipsis;\">"
    + ((stack1 = alias1(((stack1 = (depth0 != null ? lookupProperty(depth0,"group") : depth0)) != null ? lookupProperty(stack1,"description") : stack1), depth0)) != null ? stack1 : "")
    + "</div> <button action=\"read-more\" style=\"display: none\">Read More</button> </div> </div> </div>  "
    + ((stack1 = lookupProperty(helpers,"if").call(alias2,(depth0 != null ? lookupProperty(depth0,"displayCalendarListMenu") : depth0),{"name":"if","hash":{},"fn":container.program(5, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":2852},"end":{"line":1,"column":3797}}})) != null ? stack1 : "")
    + " "
    + ((stack1 = lookupProperty(helpers,"compare").call(alias2,(depth0 != null ? lookupProperty(depth0,"mode") : depth0),"calendar",{"name":"compare","hash":{},"fn":container.program(8, data, 0),"inverse":container.program(10, data, 0),"data":data,"loc":{"start":{"line":1,"column":3798},"end":{"line":1,"column":4784}}})) != null ? stack1 : "")
    + " <div class=\"scroll-trigger\"></div> <div class=\"content footer\"> <div name=\"page-footer\"></div> </div> </main> <div name=\"membership-cta\"></div> ";
},"usePartial":true,"useData":true});

this["Templates"]["Pages/Group/noEvents"] = Handlebars.template({"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " "
    + ((stack1 = container.invokePartial(lookupProperty(partials,"GroupEmptyState"),depth0,{"name":"GroupEmptyState","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + " ";
},"usePartial":true,"useData":true});

return this["Templates"];

});
//# sourceMappingURL=Group.js.map